import {
  DashboardFederationDocument,
  DashboardFederationDocumentFolder,
  DashboardLeagueDocument,
  DashboardLeagueDocumentFolder,
  DashboardPlatformDocument,
  DashboardPlatformDocumentFolder,
} from "@/api/__generated__/schemas";
import { DocumentFolderFragment, DocumentFragment, PlatformWithDocumentsFragment } from "@/graphql";
import { Document, DocumentFolder } from "@/types";

/**
 * The federation, platform and league REST DTOs for documents/folders are structurally identical
 * (same field names; `id` is a numeric row id on documents, a uuid string on folders). These REST
 * variants of the shared mappers turn any of those entities' detail payloads into the neutral
 * `Document` / `DocumentFolder` domain shapes consumed by the documents manager.
 */
type ApiDocument =
  | DashboardFederationDocument
  | DashboardPlatformDocument
  | DashboardLeagueDocument;
type ApiDocumentFolder =
  | DashboardFederationDocumentFolder
  | DashboardPlatformDocumentFolder
  | DashboardLeagueDocumentFolder;

export const mapToDocumentFromApi = (document: ApiDocument): Document => ({
  _type: "document",
  id: String(document.id),
  title: document.title ?? "",
  description: document.description ?? "",
  fileId: document.fileId ?? null,
  url: document.downloadUrl ?? "",
  downloadUrl: document.downloadUrl ?? "",
  isPrivate: document.isPrivate,
  sortingPosition: document.sortingPosition,
  folderId: document.folderId ?? null,
  fileSize: document.fileSize ?? null,
  contentType: document.contentType ?? null,
});

export const mapToDocumentFolderFromApi = (folder: ApiDocumentFolder): DocumentFolder => ({
  _type: "documentFolder",
  id: folder.id,
  title: folder.title,
  isPrivate: folder.isPrivate,
  sortingPosition: folder.sortingPosition,
  documents: (folder.documents ?? []).map(mapToDocumentFromApi).sort(bySortingPosition),
});

export const mapToDocumentsAndFoldersFromApi = (source: {
  documents: ApiDocument[];
  documentFolders: ApiDocumentFolder[];
}): { documents: Document[]; documentFolders: DocumentFolder[] } => ({
  documents: source.documents.map(mapToDocumentFromApi).sort(bySortingPosition),
  documentFolders: source.documentFolders.map(mapToDocumentFolderFromApi).sort(bySortingPosition),
});

/**
 * The `Document` / `DocumentFolder` GraphQL types are shared across entities (federations,
 * platforms, …). These mappers turn the generated `document` / `documentFolder` fragment shapes
 * into the neutral `Document` / `DocumentFolder` domain shapes consumed by the documents manager;
 * every fragment that selects those relations spreads the shared fragments, so they are all
 * assignable here.
 */
const bySortingPosition = <T extends { sortingPosition: number }>(a: T, b: T): number =>
  a.sortingPosition - b.sortingPosition;

export const mapToDocument = (document: DocumentFragment): Document => ({
  _type: "document",
  id: String(document.id),
  title: document.title ?? "",
  description: document.description ?? "",
  fileId: document.fileId ?? null,
  url: document.url ?? "",
  downloadUrl: document.downloadUrl ?? "",
  isPrivate: document.isPrivate,
  sortingPosition: document.sortingPosition,
  folderId: document.folderId ?? null,
  fileSize: document.fileSize ?? null,
  contentType: document.contentType ?? null,
});

export const mapToDocumentFolder = (folder: DocumentFolderFragment): DocumentFolder => ({
  _type: "documentFolder",
  id: folder.id,
  title: folder.title,
  isPrivate: folder.isPrivate,
  sortingPosition: folder.sortingPosition,
  documents: (folder.documents ?? []).map(mapToDocument).sort(bySortingPosition),
});

export const mapToDocumentsAndFolders = (
  source: Pick<PlatformWithDocumentsFragment, "documents" | "documentFolders">
): { documents: Document[]; documentFolders: DocumentFolder[] } => ({
  documents: (source.documents ?? []).map(mapToDocument).sort(bySortingPosition),
  documentFolders: (source.documentFolders ?? []).map(mapToDocumentFolder).sort(bySortingPosition),
});
